import { useEffect, useRef, useState } from "react";
import { MapContainer, TileLayer, Marker, useMap, useMapEvents } from "react-leaflet";
import { useNavigate } from "react-router-dom";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { toast } from "sonner";
import { Loader2, MapPin, Save, LocateFixed, Search, X, Clock } from "lucide-react";
import { api } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import PermissionGateModal from "./PermissionGateModal";
import {
    requestLocationPermission, getCurrentLocation,
} from "../lib/geolocation";

delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
    iconRetinaUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png",
    iconUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png",
    shadowUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png",
});

const DEFAULT = [17.385, 78.4867]; // Hyderabad fallback

function ClickPicker({ onPick }) {
    useMapEvents({ click(e) { onPick([e.latlng.lat, e.latlng.lng]); } });
    return null;
}
function Recenter({ to }) {
    const map = useMap();
    useEffect(() => { if (to) map.setView(to, Math.max(map.getZoom(), 15)); }, [to, map]);
    return null;
}

export default function AddressPickerModal({ initial, onClose, onSaved }) {
    const startLat = initial?.lat ?? DEFAULT[0];
    const startLng = initial?.lng ?? DEFAULT[1];
    const isEdit = !!initial?.id;
    const [pos, setPos] = useState([startLat, startLng]);
    // Root-cause fix (user-reported: "not serviceable" shown on every
    // launch) — ``pos`` starts at the hardcoded ``DEFAULT`` Hyderabad
    // fallback for brand-new addresses (just a starting map camera
    // position). If a customer typed their address into the text
    // fields without ever touching the map/search/current-location,
    // ``save()`` used to persist that DEFAULT coordinate verbatim as
    // if it were their real position. ZoneOfflineBanner then compares
    // that fake coordinate against the tenant's zones on every future
    // launch — permanently and incorrectly reporting the (possibly
    // genuinely serviceable) address as out-of-area, since it's really
    // checking against a location the customer never actually gave us.
    // ``coordsConfirmed`` tracks whether ``pos`` reflects a real,
    // user-initiated pick (map tap/drag, search result, or current-
    // location) rather than just the untouched default. Two cases
    // start already-confirmed: editing an existing address that has
    // real saved coords, AND CustomerHeader's "current location" flow
    // (see its ``setPicker({mode:"current", lat, lng})`` call), which
    // opens this modal PRE-FILLED with a genuine GPS fix obtained
    // before the modal even mounted — not the map-camera default.
    // Both cases pass real lat/lng through ``initial``; a brand-new
    // address opened via "+ Add new" (``{mode:"manual"}``, no lat/lng)
    // correctly starts unconfirmed.
    const [coordsConfirmed, setCoordsConfirmed] = useState(
        initial?.lat != null && initial?.lng != null,
    );
    const [reverse, setReverse] = useState(null);
    // Pre-populated when editing — empty otherwise. We deliberately
    // honour an existing label casing ("Home" vs "home") so the
    // round-trip with /addresses (which stores the canonical case)
    // doesn't flip the chip selection on every re-open.
    const [form, setForm] = useState({
        label: initial?.label || "Home",
        line1: initial?.line1 || "",
        line2: initial?.line2 || "",
        landmark: initial?.landmark || "",
        city: initial?.city || "",
        state: initial?.state || "",
        pincode: initial?.pincode || "",
        receiver_name: initial?.receiver_name || "",
        receiver_mobile: initial?.receiver_mobile || "",
        is_default: initial?.is_default ?? true,
    });
    const [busy, setBusy] = useState(false);
    const [locBusy, setLocBusy] = useState(false);
    // First-pass guard so the reverse-geocode doesn't overwrite a
    // populated form (edit-mode landing on a saved address) on mount.
    // We let manual map taps / search / current-location re-populate.
    const [pinTouched, setPinTouched] = useState(!isEdit);
    // Play Store / App Store compliance — in-context permission gate
    // (iter49). ``locGate`` is one of: null | "prompt" | "denied".
    // When null no modal renders; otherwise PermissionGateModal opens
    // in the matching state.
    const [locGate, setLocGate] = useState(null);
    const { user } = useAuth();
    const nav = useNavigate();

    // Reverse-geocode the dropped pin via Nominatim
    useEffect(() => {
        if (!pinTouched) return;        // edit-mode: don't clobber saved fields on mount
        let cancelled = false;
        const u = `https://nominatim.openstreetmap.org/reverse?format=json&lat=${pos[0]}&lon=${pos[1]}&zoom=18&addressdetails=1`;
        fetch(u, { headers: { Accept: "application/json" } })
            .then((r) => {
                // A rate-limited/erroring response isn't guaranteed to be
                // JSON — bail before .json() so the failure lands in the
                // .catch() below instead of throwing a confusing parse error.
                if (!r.ok) throw new Error(`nominatim reverse ${r.status}`);
                return r.json();
            })
            .then((j) => {
                if (cancelled) return;
                setReverse(j);
                const a = j.address || {};
                const autoLine1 = j.display_name?.split(",").slice(0, 2).join(", ") || "";
                // Always refresh address fields from the pin (overwrite previous auto-fill)
                setForm((f) => ({
                    ...f,
                    line1: autoLine1,
                    city: a.city || a.town || a.village || a.county || "",
                    state: a.state || "",
                    pincode: a.postcode || "",
                }));
            })
            .catch(() => {});
        return () => { cancelled = true; };
    }, [pos, pinTouched]);

    const handleUseCurrentLocation = async () => {
        // iter54.5: ``Geo.checkPermissions()`` from the Capacitor
        // plugin has been observed to block the JS event loop on
        // production Android devices (the bridge call wedges, and
        // even our ``_withTimeout`` race can't fire because setTimeout
        // never gets event-loop time). Skip the upfront permission
        // probe entirely — ``getCurrentLocation`` already races the
        // Capacitor plugin against the WebView's ``navigator.geolocation``
        // in parallel, AND maps a "denied" / "permission" error from
        // EITHER arm to the same gate-modal flow we used before. So
        // the upfront check buys us nothing and just adds another
        // place to hang.
        const t0 = Date.now();
        toast.loading("Locating…",
                      { id: "geo-locating", duration: 12_000 });
        const tick = setInterval(() => {
            const buf = (typeof window !== "undefined" && window.__geo_dbg) || [];
            const lastStage = buf.length ? buf[buf.length - 1].stage : "(no stages)";
            toast.loading(`Locating… [${lastStage}] ${((Date.now()-t0)/1000).toFixed(0)}s`,
                          { id: "geo-locating", duration: 12_000 });
        }, 2000);
        try {
            await fetchCurrentCoords();
        } catch (err) {
            const buf = (typeof window !== "undefined" && window.__geo_dbg) || [];
            const trail = buf.slice(-5).map((e) => e.stage).join(" → ");
            toast.error(`Location error: ${err?.message || err}  [${trail}]`,
                        { duration: 12000 });
        } finally {
            clearInterval(tick);
            toast.dismiss("geo-locating");
        }
    };

    const fetchCurrentCoords = async () => {
        setLocBusy(true);
        const r = await getCurrentLocation();
        setLocBusy(false);
        if (r.state === "granted" && r.coords) {
            setPinTouched(true);
            setCoordsConfirmed(true);
            setPos([r.coords.lat, r.coords.lng]);
            toast.success("Pinned to your current location");
            return;
        }
        if (r.state === "denied") { setLocGate("denied"); return; }
        if (r.state === "unsupported") {
            toast.error("Location not supported on this device");
            return;
        }
        // iter54: surface the diagnostics buffer in the error toast on
        // native so we can see the actual failure (timeout / WebView
        // block / plugin-missing) without adb. Strip in 1-2 versions
        // once the issue stops being reported.
        let debugTail = "";
        try {
            const buf = (typeof window !== "undefined" && window.__geo_dbg) || [];
            const lastStages = buf.slice(-4).map((e) => e.stage).join(" → ");
            if (lastStages) debugTail = `  [${lastStages}]`;
        } catch { /* ignore */ }
        toast.error((r.error || "Couldn't fetch location — try again outside or near a window") + debugTail,
                    { duration: 10000 });
    };

    const save = async () => {
        if (!form.line1 || !form.city || !form.pincode) return toast.error("Address line, city & pincode are required");
        const payload = {
            label: form.label,
            line1: form.line1,
            line2: form.line2 || null,
            landmark: form.landmark || null,
            city: form.city,
            state: form.state || "TS",
            pincode: form.pincode,
            receiver_name: form.receiver_name || null,
            receiver_mobile: form.receiver_mobile || null,
            // Only persist ``pos`` when it reflects a real, confirmed
            // pick — never the untouched DEFAULT map-camera fallback
            // (see ``coordsConfirmed`` above). Serviceability then
            // falls back to the pincode the customer actually typed,
            // which is real regardless of whether they touched the map.
            lat: coordsConfirmed ? pos[0] : null,
            lng: coordsConfirmed ? pos[1] : null,
            is_default: !!form.is_default,
        };
        if (!user) {
            // Anonymous visitor: stash the picked address locally and bounce to
            // login. The home page will pick it up on return and persist it.
            try {
                localStorage.setItem("bizil_pending_address", JSON.stringify({
                    ...payload,
                    is_default: true,
                }));
            } catch { /* ignore quota */ }
            toast.info("Please login to save your address");
            nav("/login", { state: { from: "/", role: "CUSTOMER" } });
            return;
        }
        setBusy(true);
        try {
            let data;
            if (isEdit) {
                const res = await api.patch(`/addresses/${initial.id}`, payload);
                data = res.data;
                toast.success("Address updated");
            } else {
                const res = await api.post("/addresses", payload);
                data = res.data;
                toast.success("Address saved");
            }
            // Broadcast so any header/marketplace listening can refresh
            // its default-address preview without a full reload.
            try { window.dispatchEvent(new Event("addresses:update")); } catch { /* ignore */ }
            onSaved && onSaved(data);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Failed to save address");
        } finally { setBusy(false); }
    };

    return (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-end sm:items-center justify-center" onClick={onClose}>
            <div className="bg-card w-full sm:max-w-lg rounded-t-3xl sm:rounded-3xl p-4 max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
                <div className="flex items-center justify-between mb-2">
                    <p className="font-bold text-lg">{isEdit ? "Edit address" : "Pin your delivery location"}</p>
                    <button onClick={onClose} className="text-sm text-muted-foreground">Cancel</button>
                </div>
                <p className="text-xs text-muted-foreground mb-3">Drag the map or tap to drop the pin. We&apos;ll auto-fill the address.</p>

                <LocationSearchBar onPick={(p) => { setPinTouched(true); setCoordsConfirmed(true); setPos(p); }} />

                <div className="relative mt-3 bg-muted rounded-2xl overflow-hidden" style={{ height: 260 }} data-testid="addr-map">
                    <MapContainer center={pos} zoom={16} style={{ height: "100%", width: "100%" }}>
                        <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" attribution="© OpenStreetMap" />
                        <Recenter to={pos} />
                        <ClickPicker onPick={(p) => { setPinTouched(true); setCoordsConfirmed(true); setPos(p); }} />
                        <Marker position={pos} />
                    </MapContainer>
                    <button
                        data-testid="addr-use-current"
                        onClick={(e) => { e.stopPropagation(); handleUseCurrentLocation(); }}
                        onMouseDown={(e) => e.stopPropagation()}
                        onTouchStart={(e) => e.stopPropagation()}
                        disabled={locBusy}
                        className="absolute z-[1000] right-3 bottom-8 bg-card border border-border shadow-md rounded-full px-3 py-2 text-xs font-bold inline-flex items-center gap-1.5 disabled:opacity-60"
                        style={{ touchAction: "manipulation" }}
                        title="Use current location">
                        {locBusy ? <Loader2 size={14} className="animate-spin" /> : <LocateFixed size={14} className="text-primary" />}
                        Current location
                    </button>
                </div>

                <div className="mt-3 bg-muted/50 rounded-xl p-3 text-xs flex items-start gap-2 text-foreground">
                    <MapPin size={14} className="text-primary mt-0.5" />
                    <span className="line-clamp-2">{reverse?.display_name || `${pos[0].toFixed(5)}, ${pos[1].toFixed(5)}`}</span>
                </div>

                <div className="mt-3 grid grid-cols-3 gap-2">
                    {["Home", "Work", "Other"].map((k) => {
                        const sel = (form.label || "").toLowerCase() === k.toLowerCase();
                        return (
                            <button key={k} data-testid={`addr-label-${k.toLowerCase()}`} onClick={() => setForm({ ...form, label: k })}
                                className={`text-xs font-bold uppercase tracking-wider rounded-xl py-2 border ${sel ? "bg-primary text-primary-foreground border-primary" : "border-border text-foreground"}`}>{k}</button>
                        );
                    })}
                </div>

                <div className="mt-3 grid grid-cols-2 gap-3">
                    <input data-testid="addr-line1" className="inp col-span-2" placeholder="House / Flat / Building no." value={form.line1} onChange={(e) => setForm({ ...form, line1: e.target.value })} />
                    <input data-testid="addr-line2" className="inp col-span-2" placeholder="Street / Area" value={form.line2} onChange={(e) => setForm({ ...form, line2: e.target.value })} />
                    <input data-testid="addr-landmark" className="inp col-span-2" placeholder="Landmark (optional)" value={form.landmark} onChange={(e) => setForm({ ...form, landmark: e.target.value })} />
                    <input data-testid="addr-city" className="inp" placeholder="City" value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} />
                    <input data-testid="addr-state" className="inp" placeholder="State" value={form.state} onChange={(e) => setForm({ ...form, state: e.target.value })} />
                    <input data-testid="addr-pincode" inputMode="numeric" className="inp col-span-2" placeholder="Pincode" value={form.pincode} onChange={(e) => setForm({ ...form, pincode: e.target.value })} />
                    <input data-testid="addr-receiver-name" className="inp" placeholder="Receiver name (optional)" value={form.receiver_name} onChange={(e) => setForm({ ...form, receiver_name: e.target.value })} />
                    <input data-testid="addr-receiver-mobile" inputMode="numeric" className="inp" placeholder="Receiver mobile (optional)" value={form.receiver_mobile} onChange={(e) => setForm({ ...form, receiver_mobile: e.target.value })} />
                </div>

                <label className="flex items-center gap-2 mt-3 cursor-pointer">
                    <input data-testid="addr-default" type="checkbox" checked={!!form.is_default} onChange={(e) => setForm({ ...form, is_default: e.target.checked })} className="w-4 h-4 accent-primary" />
                    <span className="text-sm">Set as default address</span>
                </label>

                <button data-testid="addr-save" disabled={busy} onClick={save}
                    className="mt-4 w-full bg-primary text-primary-foreground rounded-xl py-3 font-bold inline-flex items-center justify-center gap-2 disabled:opacity-50">
                    {busy ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />} {isEdit ? "Update address" : "Save address"}
                </button>
            </div>
            {locGate && (
                <PermissionGateModal
                    permission="location"
                    state={locGate}
                    onClose={() => setLocGate(null)}
                    onRequest={async () => {
                        // requestLocationPermission() returns a structured
                        // {state, coords, error} object — matches the
                        // contract used by CustomerHeader/AdminZones/
                        // ShopMapPicker. When it already has coords
                        // (granted + native fetched a position in the same
                        // round trip) apply them directly instead of
                        // triggering a second getCurrentLocation() call.
                        const next = await requestLocationPermission();
                        if (next.state === "granted") {
                            setLocGate(null);
                            if (next.coords) {
                                setPinTouched(true);
                                setCoordsConfirmed(true);
                                setPos([next.coords.lat, next.coords.lng]);
                                toast.success("Pinned to your current location");
                            } else {
                                fetchCurrentCoords();
                            }
                        } else if (next.state === "denied") {
                            setLocGate("denied");
                        }
                        return next;
                    }}
                />
            )}
            <style>{`.inp{background:hsl(var(--muted));color:hsl(var(--foreground));border-radius:0.75rem;padding:0.6rem 0.75rem;outline:none;font-size:0.875rem;width:100%}.inp::placeholder{color:hsl(var(--muted-foreground))}`}</style>
        </div>
    );
}

export function LocationSearchBar({ onPick, placeholder = "Search a city, area, building…", testid = "loc-search" }) {
    const [q, setQ] = useState("");
    const [results, setResults] = useState([]);
    const [busy, setBusy] = useState(false);
    const [open, setOpen] = useState(false);
    // Distinguishes "Nominatim genuinely has no match" from "the request
    // failed" (network error / rate-limit) — both used to collapse into an
    // empty dropdown, which reads to the user as "this area doesn't exist"
    // (user-reported: "certain local areas cannot be found").
    const [error, setError] = useState(false);
    const [recent, setRecent] = useState(() => {
        try { return JSON.parse(localStorage.getItem("bizil_loc_recent") || "[]"); } catch { return []; }
    });
    const tRef = useRef(null);

    useEffect(() => {
        if (!q || q.trim().length < 3) { setResults([]); setError(false); return; }
        clearTimeout(tRef.current);
        tRef.current = setTimeout(async () => {
            setBusy(true);
            setError(false);
            try {
                const u = `https://nominatim.openstreetmap.org/search?format=json&addressdetails=1&limit=6&countrycodes=in&q=${encodeURIComponent(q)}`;
                const r = await fetch(u, { headers: { Accept: "application/json" } });
                if (!r.ok) throw new Error(`nominatim search ${r.status}`);
                const j = await r.json();
                setResults(j || []);
                setOpen(true);
            } catch {
                setResults([]);
                setError(true);
                setOpen(true);
            } finally { setBusy(false); }
        }, 350);
        return () => clearTimeout(tRef.current);
    }, [q]);

    const saveRecent = (entry) => {
        const next = [entry, ...recent.filter((r) => r.place_id !== entry.place_id)].slice(0, 5);
        setRecent(next);
        try { localStorage.setItem("bizil_loc_recent", JSON.stringify(next)); } catch { /* ignore */ }
    };

    const choose = (r) => {
        setQ(r.display_name);
        setOpen(false);
        const entry = { place_id: r.place_id, display_name: r.display_name, lat: r.lat, lon: r.lon };
        saveRecent(entry);
        onPick([parseFloat(r.lat), parseFloat(r.lon)]);
    };

    const focusOpen = () => {
        if (q && results.length) setOpen(true);
        else if (!q && recent.length) setOpen(true);
    };

    const showRecent = open && !q && recent.length > 0;
    const showResults = open && q && results.length > 0;
    const showError = open && q && !busy && error && results.length === 0;

    return (
        <div className="relative">
            <div className="flex items-center gap-2 bg-muted rounded-xl px-3 py-2.5">
                <Search size={16} className="text-muted-foreground" />
                <input
                    data-testid={testid}
                    value={q}
                    onChange={(e) => { setQ(e.target.value); setOpen(true); }}
                    onFocus={focusOpen}
                    placeholder={placeholder}
                    className="flex-1 bg-transparent text-sm outline-none"
                />
                {busy && <Loader2 size={14} className="animate-spin text-muted-foreground" />}
                {q && !busy && (
                    <button onClick={() => { setQ(""); setResults([]); setError(false); setOpen(recent.length > 0); }} className="text-muted-foreground"><X size={14} /></button>
                )}
            </div>
            {(showRecent || showResults || showError) && (
                <div className="absolute z-[450] left-0 right-0 mt-1 bg-card border border-border rounded-xl shadow-lg max-h-60 overflow-y-auto">
                    {showError && (
                        <p className="px-3 py-2.5 text-xs text-muted-foreground" data-testid={`${testid}-error`}>
                            Search is temporarily unavailable. Check your connection and try again.
                        </p>
                    )}
                    {showRecent && (
                        <>
                            <p className="px-3 py-1.5 text-[10px] uppercase tracking-wider font-bold text-muted-foreground bg-muted/40">Recent</p>
                            {recent.map((r) => (
                                <button
                                    key={`rec-${r.place_id}`}
                                    data-testid={`${testid}-recent`}
                                    onClick={() => choose(r)}
                                    className="w-full text-left px-3 py-2 text-xs hover:bg-muted border-b last:border-0 border-border inline-flex items-start gap-2">
                                    <Clock size={12} className="mt-0.5 text-muted-foreground shrink-0" />
                                    <span className="line-clamp-2">{r.display_name}</span>
                                </button>
                            ))}
                        </>
                    )}
                    {showResults && results.map((r) => (
                        <button
                            key={`${r.place_id}`}
                            data-testid={`${testid}-result`}
                            onClick={() => choose(r)}
                            className="w-full text-left px-3 py-2 text-xs hover:bg-muted border-b last:border-0 border-border">
                            <p className="font-semibold line-clamp-1">{r.display_name?.split(",").slice(0, 2).join(", ")}</p>
                            <p className="text-muted-foreground line-clamp-1">{r.display_name}</p>
                        </button>
                    ))}
                </div>
            )}
        </div>
    );
}
